import React, { useState, useEffect, useRef } from 'react';

const App = () => {
  // Navigation State
  const [currentView, setCurrentView] = useState('home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Branch State
  const [activeBranch, setActiveBranch] = useState('kalyanpur');

  // Inject external fonts and icons on mount
  useEffect(() => {
    if (!document.getElementById('fa-stylesheet')) {
      const linkFa = document.createElement('link');
      linkFa.id = 'fa-stylesheet';
      linkFa.rel = 'stylesheet';
      linkFa.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
      document.head.appendChild(linkFa);
    }
    if (!document.getElementById('google-fonts')) {
      const linkFonts = document.createElement('link');
      linkFonts.id = 'google-fonts';
      linkFonts.rel = 'stylesheet';
      linkFonts.href = 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap';
      document.head.appendChild(linkFonts);
    }
  }, []);

  const branches = {
    kalyanpur: {
      id: 'kalyanpur',
      title: "Kalyanpur Branch Hub",
      subtitle: "Main Digital Counter",
      desc: "Strategically designed to fulfill all complex legal digital filings, business setup documentation, and urgent security copy generations.",
      phone: "+91 9696156200",
      address: "Kalyanpur Main Road, Kanpur, UP",
      icon: "fa-building",
      colorClass: "indigo"
    },
    kakadeo: {
      id: 'kakadeo',
      title: "Kakadeo Student Center",
      subtitle: "Coaching Hub Zone",
      desc: "Our dedicated high-speed operational node focused on student competitive forms processing, swift online admissions, and instantly cleared driving licenses.",
      phone: "+91 7388367658",
      address: "Infront of Apna Bhojnalaya, Kakadeo, Kanpur",
      icon: "fa-store",
      colorClass: "purple"
    }
  };

  const navigate = (view, sectionId = null) => {
    setCurrentView(view);
    setIsMobileMenuOpen(false);
    
    if (sectionId && view === 'home') {
      setTimeout(() => {
        const section = document.getElementById(sectionId);
        if (section) {
          const y = section.getBoundingClientRect().top + window.scrollY - 100;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const LiveTicker = () => {
    const tickerRef = useRef(null);

    useEffect(() => {
      const tickerElement = tickerRef.current;
      if (!tickerElement) return;

      let pos = tickerElement.parentElement.offsetWidth;
      let animationFrameId;

      const animate = () => {
        pos -= 1.5;
        if (pos < -tickerElement.offsetWidth) {
          pos = tickerElement.parentElement.offsetWidth;
        }
        tickerElement.style.transform = `translateX(${pos}px)`;
        animationFrameId = requestAnimationFrame(animate);
      };

      animate();
      return () => cancelAnimationFrame(animationFrameId);
    }, []);

    return (
      <section className="py-6 bg-[#090d16] border-y border-gray-900 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between">
          <div className="flex items-center space-x-3 mb-4 md:mb-0 w-full md:w-auto shrink-0">
            <span className="w-3 h-3 rounded-full bg-emerald-500 live-pulse"></span>
            <span className="font-extrabold uppercase tracking-wider text-xs sm:text-sm text-gray-400 whitespace-nowrap">Live Feed:</span>
          </div>
          <div className="w-full overflow-hidden relative h-6 md:h-8 flex items-center">
            <div ref={tickerRef} className="absolute whitespace-nowrap text-xs sm:text-sm text-indigo-300 font-medium">
              ?? Order #3928 processed for Aadhaar Correction at Kakadeo Branch! &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; ?? New Service Launched: 4-Hour Urgent Driving License Print! &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; ?? Secure Watermark cloud syncing updated successfully.
            </div>
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className="font-sans text-[#F8FAFC] min-h-screen flex flex-col overflow-x-hidden antialiased" style={{ backgroundColor: '#0B0F19', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      
      {/* Global Styles for Custom Gradients & Animations */}
      <style>{`
        .glass-card {
            background: rgba(17, 24, 39, 0.7);
            backdrop-filter: blur(12px);
            -webkit-backdrop-filter: blur(12px);
            border: 1px solid rgba(255, 255, 255, 0.08);
        }
        .text-gradient {
            background: linear-gradient(135deg, #6366F1 0%, #A855F7 50%, #10B981 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        .btn-gradient {
            background: linear-gradient(135deg, #4A00E0 0%, #8E2DE2 100%);
        }
        .btn-gradient:hover {
            background: linear-gradient(135deg, #5c14e6 0%, #9a3df0 100%);
        }
        .live-pulse {
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
            animation: pulse 1.5s infinite;
        }
        @keyframes pulse {
            0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
            70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(16, 185, 129, 0); }
            100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
        }
      `}</style>

      {/* NAVIGATION BAR */}
      <nav className="fixed top-0 left-0 w-full z-50 glass-card border-b border-gray-800 px-6 py-4 transition-all duration-300">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('home')}>
            <div className="h-10 w-10 btn-gradient rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-extrabold text-xl tracking-wider">VP</span>
            </div>
            <span className="text-2xl font-black tracking-tight text-white">VP Cafe <span className="text-indigo-400">Connect</span></span>
          </div>
          
          {/* Desktop Links */}
          <div className="hidden md:flex items-center space-x-8 font-medium text-gray-300">
            <button onClick={() => navigate('home')} className="hover:text-white transition">Home</button>
            <button onClick={() => navigate('home', 'services')} className="hover:text-white transition">Services</button>
            <button onClick={() => navigate('home', 'branches')} className="hover:text-white transition">Branches</button>
            <div className="flex items-center space-x-4 pl-2 border-l border-gray-700">
              <button className="hover:text-white transition font-semibold text-indigo-400">Customer Login</button>
              <a href="https://vpcafe.in/panel/" target="_blank" rel="noreferrer" className="btn-gradient text-white px-5 py-2.5 rounded-xl font-semibold shadow-lg hover:shadow-indigo-500/25 transition-all inline-block">
                Admin Login
              </a>
            </div>
          </div>

          {/* Mobile Menu Btn */}
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden text-gray-300 hover:text-white focus:outline-none">
            <i className="fa-solid fa-bars text-2xl"></i>
          </button>
        </div>

        {/* Mobile Dropdown */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pt-4 border-t border-gray-800 flex flex-col space-y-4 pb-2">
            <button onClick={() => navigate('home')} className="text-left text-gray-300 hover:text-white py-1 px-2">Home</button>
            <button onClick={() => navigate('home', 'services')} className="text-left text-gray-300 hover:text-white py-1 px-2">Services</button>
            <button onClick={() => navigate('home', 'branches')} className="text-left text-gray-300 hover:text-white py-1 px-2">Branches</button>
            {/* FIXED: Replaced onClick button with SEO-friendly <a> tag */}
            <a href="/terms" className="text-left text-gray-300 hover:text-white py-1 px-2 block">Terms & Conditions</a>
            <a href="/privacy" className="text-left text-gray-300 hover:text-white py-1 px-2 block">Privacy Policy</a>
            <div className="border-t border-gray-800 pt-3 flex flex-col space-y-3">
              <button className="text-left text-indigo-400 font-semibold hover:text-white py-1 px-2">Customer Login</button>
              <a href="https://vpcafe.in/panel/" target="_blank" rel="noreferrer" className="btn-gradient text-center text-white px-5 py-2.5 rounded-xl font-semibold w-full block">Admin Login</a>
            </div>
          </div>
        )}
      </nav>

      {/* MAIN CONTENT AREA */}
      <main className="flex-grow pt-24">
        
        {currentView === 'home' && (
          <div className="animate-fade-in">
            {/* HERO SECTION */}
            <section className="relative py-12 md:py-20 px-6 max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between">
              {/* Background ambient glows */}
              <div className="absolute -top-40 -left-40 w-96 h-96 bg-indigo-600 rounded-full filter blur-[150px] opacity-20 pointer-events-none"></div>
              <div className="absolute top-40 right-10 w-80 h-80 bg-emerald-500 rounded-full filter blur-[150px] opacity-15 pointer-events-none"></div>

              <div className="lg:w-1/2 space-y-6 text-center lg:text-left z-10 mt-10 md:mt-0">
                <div className="inline-flex items-center space-x-2 bg-gray-900 border border-gray-800 px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium text-indigo-300">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 live-pulse"></span>
                  <span>Kanpur's Premium Digital Seva Platform</span>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight text-white">
                  Fast & Secure <br />
                  <span className="text-gradient">Digital Cafe Solutions</span> <br />
                  At Your Fingertips.
                </h1>
                <p className="text-gray-400 text-base sm:text-lg max-w-xl mx-auto lg:mx-0">
                  Apply for Government Forms, IDs, fast-track updates, corrections, and absolute secure data processing via our premium high-tech hubs.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-4">
                  <button onClick={() => navigate('home', 'services')} className="w-full sm:w-auto text-center btn-gradient text-white px-8 py-4 rounded-2xl font-bold shadow-xl hover:-translate-y-1 transition duration-300">
                    Explore Services <i className="fa-solid fa-arrow-right ml-2"></i>
                  </button>
                </div>
              </div>

              {/* HERO APP PREVIEW MOCKUP */}
              <div className="lg:w-1/2 mt-16 lg:mt-0 flex justify-center w-full z-10">
                <div className="glass-card p-6 rounded-[32px] w-full max-w-sm shadow-2xl relative border border-gray-800 transform hover:scale-[1.02] transition-transform duration-500">
                  <div className="absolute -right-4 -top-4 btn-gradient text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-md z-20">App Active</div>
                  
                  {/* Inner Screen */}
                  <div className="bg-[#0e1322] rounded-2xl p-4 space-y-4 border border-gray-800">
                    <div className="flex justify-between items-center text-sm">
                      <div className="flex items-center space-x-2">
                        <i className="fa-solid fa-location-dot text-indigo-400"></i>
                        <span className="font-bold text-white">Kalyanpur Branch</span>
                      </div>
                      <i className="fa-regular fa-bell text-gray-400"></i>
                    </div>

                    <div className="p-4 rounded-xl bg-gradient-to-br from-indigo-900/40 to-purple-900/40 border border-indigo-500/20">
                      <p className="text-xs text-indigo-300 tracking-wider uppercase font-semibold">VP Cafe Cloud Service</p>
                      <p className="text-xl font-black mt-1 text-white">Secure Copy Terminal</p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-gray-900/60 rounded-xl border border-gray-800 text-center flex flex-col items-center justify-center">
                        <i className="fa-solid fa-file-invoice text-orange-400 text-xl mb-2"></i>
                        <p className="text-xs font-semibold text-gray-200">Govt Forms</p>
                      </div>
                      <div className="p-3 bg-gray-900/60 rounded-xl border border-gray-800 text-center flex flex-col items-center justify-center">
                        <i className="fa-solid fa-id-card text-blue-400 text-xl mb-2"></i>
                        <p className="text-xs font-semibold text-gray-200">Govt IDs</p>
                      </div>
                      <div className="p-3 bg-gray-900/60 rounded-xl border border-gray-800 text-center flex flex-col items-center justify-center">
                        <i className="fa-solid fa-wand-magic-sparkles text-pink-400 text-xl mb-2"></i>
                        <p className="text-xs font-semibold text-gray-200">Corrections</p>
                      </div>
                      <div className="p-3 bg-gray-900/60 rounded-xl border border-gray-800 text-center flex flex-col items-center justify-center">
                        <i className="fa-solid fa-bolt text-emerald-400 text-xl mb-2"></i>
                        <p className="text-xs font-semibold text-gray-200">Urgent Print</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <LiveTicker />

            {/* SERVICES SECTION */}
            <section id="services" className="py-20 px-6 max-w-7xl mx-auto relative">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-white">Full-Suite Digital & Cyber Services</h2>
                <p className="text-gray-400 mt-3 text-lg">No more endless queuing. Pick a department, process online, and grab your secure copies in record time.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                <div className="glass-card p-6 lg:p-8 rounded-3xl group hover:border-indigo-500/50 transition-all duration-300 hover:-translate-y-1">
                  <div className="w-14 h-14 rounded-2xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center text-orange-400 text-2xl group-hover:scale-110 transition duration-300">
                    <i className="fa-solid fa-file-invoice"></i>
                  </div>
                  <h3 className="text-xl font-bold mt-6 text-white">Government Forms</h3>
                  <p className="text-gray-400 mt-2 text-sm leading-relaxed">State & Central schemes, online job filings, scholarship application setups with validation.</p>
                </div>
                <div className="glass-card p-6 lg:p-8 rounded-3xl group hover:border-blue-500/50 transition-all duration-300 hover:-translate-y-1">
                  <div className="w-14 h-14 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 text-2xl group-hover:scale-110 transition duration-300">
                    <i className="fa-solid fa-id-card"></i>
                  </div>
                  <h3 className="text-xl font-bold mt-6 text-white">Official Govt IDs</h3>
                  <p className="text-gray-400 mt-2 text-sm leading-relaxed">Aadhaar registrations, New PAN card allotment, Driving License applications.</p>
                </div>
                <div className="glass-card p-6 lg:p-8 rounded-3xl group hover:border-emerald-500/50 transition-all duration-300 hover:-translate-y-1">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 text-2xl group-hover:scale-110 transition duration-300">
                    <i className="fa-solid fa-cloud-arrow-up"></i>
                  </div>
                  <h3 className="text-xl font-bold mt-6 text-white">My Documents Vault</h3>
                  <p className="text-gray-400 mt-2 text-sm leading-relaxed">Safely fetch, store, track, and pull copy logs of your previously handled services via a cloud environment synced securely.</p>
                </div>
              </div>
            </section>

            {/* BRANCHES SECTION */}
            <section id="branches" className="py-20 bg-[#090d16] px-6">
              <div className="max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
                  <div className="lg:w-1/2 space-y-6">
                    <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-white">Our Fully Operational Branches in Kanpur</h2>
                    <p className="text-gray-400 text-base sm:text-lg">Select a branch to explore current timing queues, specific local contact helpdesks, and live status connections.</p>
                    
                    <div className="flex flex-col space-y-4 pt-4">
                      {Object.values(branches).map((branch) => (
                        <button 
                          key={branch.id}
                          onClick={() => setActiveBranch(branch.id)} 
                          className={`flex items-center justify-between p-4 sm:p-5 rounded-2xl glass-card text-left transition duration-300 ${activeBranch === branch.id ? `border-${branch.colorClass}-500/60 opacity-100` : 'border-transparent opacity-60 hover:opacity-100'}`}
                        >
                          <div className="flex items-center space-x-4">
                            <div className={`w-10 h-10 rounded-xl bg-${branch.colorClass}-500/10 flex items-center justify-center text-${branch.colorClass}-400`}>
                              <i className={`fa-solid ${branch.icon}`}></i>
                            </div>
                            <div>
                              <p className="font-bold text-white">{branch.title}</p>
                              <p className="text-xs text-gray-400">{branch.subtitle}</p>
                            </div>
                          </div>
                          <i className="fa-solid fa-chevron-right text-gray-500"></i>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Active Branch Detail Card */}
                  <div className="lg:w-1/2 w-full flex justify-center">
                    <div className="glass-card p-6 md:p-8 rounded-[32px] w-full max-w-md border border-gray-800 relative">
                      <div className="absolute right-6 top-6 px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold rounded-full">Open Now</div>
                      <h4 className="text-xl md:text-2xl font-extrabold text-white pr-20">{branches[activeBranch].title}</h4>
                      <p className="text-gray-400 text-sm mt-3">{branches[activeBranch].desc}</p>
                      
                      <div className="border-t border-gray-800 my-6 pt-6 space-y-4">
                        <div className="flex items-center space-x-3 text-sm text-gray-300">
                          <i className="fa-regular fa-clock text-indigo-400 w-5 text-center"></i>
                          <span>09:00 AM - 08:30 PM (Daily)</span>
                        </div>
                        <div className="flex items-center space-x-3 text-sm text-gray-300">
                          <i className="fa-solid fa-phone text-indigo-400 w-5 text-center"></i>
                          <span>{branches[activeBranch].phone}</span>
                        </div>
                        <div className="flex items-center space-x-3 text-sm text-gray-300">
                          <i className="fa-solid fa-location-dot text-indigo-400 w-5 text-center"></i>
                          <span>{branches[activeBranch].address}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}

      </main>

      {/* FOOTER */}

{/* FOOTER */}
<footer className="border-t border-gray-900 bg-[#070a10] py-12 px-6 text-gray-500 text-sm mt-auto z-10 relative">
  <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">

    {/* Brand */}
    <div className="flex flex-col space-y-4">
      <div className="flex items-center space-x-3">
        <div className="h-8 w-8 bg-brand rounded-lg flex items-center justify-center text-white font-bold text-sm">
          VP
        </div>
        <span className="text-white font-bold text-lg">VP Cafe Connect</span>
      </div>

      <p className="text-gray-500 text-xs max-w-xs">
        Kanpur's premium digital seva and cyber facility, providing
        ultra-fast, secure, and reliable document processing.
      </p>

      {/* Google Verification Branding */}
      <p className="text-gray-400 text-xs max-w-xs">
       VP Cafe Connect is the official digital services platform operated by
        VP Cafe (vpcafe.in).
      </p>
    </div>

    {/* Company */}
    <div className="flex flex-col space-y-3">
      <h4 className="text-gray-300 font-bold uppercase tracking-wider text-xs mb-1">
        Company
      </h4>

      <button
        onClick={() => navigate('home')}
        className="text-left hover:text-white transition w-fit"
      >
        Home
      </button>

      <button
        onClick={() => navigate('home', 'branches')}
        className="text-left hover:text-white transition w-fit"
      >
        Locate Branches
      </button>

      <button
        onClick={() => navigate('home', 'services')}
        className="text-left hover:text-white transition w-fit"
      >
        All Services
      </button>
    </div>

    {/* Legal */}
    <div className="flex flex-col space-y-3">
      <h4 className="text-gray-300 font-bold uppercase tracking-wider text-xs mb-1">
        Legal & Privacy
      </h4>

      <a
        href="/terms"
        className="text-left hover:text-white transition flex items-center space-x-2 w-fit"
      >
        <i className="fa-solid fa-scale-balanced text-xs"></i>
        <span>Terms & Conditions</span>
      </a>

      <a
        href="/privacy"
        className="text-left hover:text-white transition flex items-center space-x-2 w-fit"
      >
        <i className="fa-solid fa-shield-halved text-xs"></i>
        <span>Privacy Policy</span>
      </a>

      <div className="pt-2 text-xs">
        <i className="fa-regular fa-envelope mr-1 text-indigo-400"></i>
        admin@vpconnect.in
      </div>
    </div>
  </div>

  <div className="max-w-7xl mx-auto border-t border-gray-800 mt-8 pt-6 flex justify-between items-center flex-col sm:flex-row gap-4">
    <p className="text-xs">
      � 2026 VP Cafe Connect. All Rights Reserved.
    </p>

    <div className="flex space-x-4">
      <a href="#" className="hover:text-white transition">
        <i className="fa-brands fa-whatsapp text-lg"></i>
      </a>

      <a href="#" className="hover:text-white transition">
        <i className="fa-brands fa-instagram text-lg"></i>
      </a>
    </div>
  </div>
</footer>
    </div>
  );
};

export default App;